/**
 * Redux Store enhancer to listen to a dispatched action.
 * Helpul for Analytics events, where we want to send an event to GA,
 * but not touch the business logic or change the state of the store.
 * Usage:
 *   const removeListener = store.addActionListener('USER_SIGNED_UP', (action) => {
 *     analytics.sendEvent('track', 'CompleteRegistration');
 *   });
 *
 * @param  {Funciton} createStore Redux createStore function
 * @return {Function}             Function that enhances the store
 */
export default function actionListenersStoreEnhancer(createStore) {
  return (reducer, initialState, enhancer) => {
    const actionListeners = {};
    const store = createStore(reducer, initialState, enhancer);
    const dispatch = store.dispatch;

    /**
     * Override original dispatch action, to call our listeners.
     * @param  {String} action Action type name.
     * @return {Mixed}         Result from original dispatch call.
     */
    store.dispatch = (action) => {
      const result = dispatch(action);

      if (
        typeof action === 'object' &&
        action.type &&
        actionListeners[action.type]
      ) {
        actionListeners[action.type].forEach((listener) => listener(action));
      }

      return result;
    };

    /**
     * Adds a new action listener
     * @param  {String}   actionType Action type name. Must match an existing action type.
     * @param  {Function} listener   Callback to be executed when the action is dispatched.
     * @return {Function}            Function that will remove the listener when called.
     */
    store.addActionListener = (actionType, listener) => {
      actionListeners[actionType] = (actionListeners[actionType] || []).concat(
        listener
      );

      // Calling this function, will remove the listener!
      return () => {
        actionListeners[actionType] = actionListeners[actionType].filter(
          (l) => l !== listener
        );
      };
    };

    return store;
  };
}
